// src/components/payment/MonerisPaymentLiveTraining.jsx
import React, { useEffect, useRef, useState } from "react";
import { Button, Alert, Spin } from "antd";
import { CreditCardOutlined, CheckCircleOutlined, VideoCameraOutlined } from "@ant-design/icons";
import liveTrainingService from '../../services/liveTraining.service';

const SDK_SRC_QA = "https://gatewayt.moneris.com/chktv2/js/chkt_v2.01.js";
const SDK_SRC_PROD = "https://gateway.moneris.com/chkt/js/chkt_v2.01.js";
const USE_PROD = true;

const MonerisPaymentLiveTraining = ({ liveTraining, onSuccess, onError, autoStart = false }) => {
    const [err, setErr] = useState("");
    const [loading, setLoading] = useState(false);
    const [processing, setProcessing] = useState(false);
    const [sdkReady, setSdkReady] = useState(!!window.monerisCheckout);
    const [ticket, setTicket] = useState(null);
    const containerRef = useRef(null);

    // Charge le SDK Moneris une seule fois
    useEffect(() => {
        if (window.monerisCheckout) {
            setSdkReady(true);
            return;
        }
        const script = document.createElement("script");
        script.src = USE_PROD ? SDK_SRC_PROD : SDK_SRC_QA;
        script.async = true;
        script.onload = () => setSdkReady(true);
        script.onerror = () => setErr("Erreur de chargement du SDK Moneris");
        document.body.appendChild(script);
    }, []);

    // Demarre automatiquement si demande
    useEffect(() => {
        if (autoStart && sdkReady && !ticket && !loading) {
            startPayment();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [autoStart, sdkReady, ticket, loading]);

    const startPayment = async () => {
        setErr("");
        setLoading(true);

        try {
            if (typeof window.monerisCheckout === "undefined") {
                throw new Error("SDK Moneris non charge");
            }

            const userStr = localStorage.getItem('user');
            const user = userStr ? JSON.parse(userStr) : null;

            if (!user || !user.id) {
                throw new Error("Utilisateur non identifie. Veuillez vous reconnecter.");
            }

            console.log("Creation du ticket Moneris pour formation live...");
            const resp = await liveTrainingService.createMonerisTicket({
                liveTraining,
                amount: liveTraining?.price || 0,
                user,
            });

            const t = resp?.ticket || resp?.data?.ticket;
            if (!t) throw new Error("Ticket Moneris introuvable");

            console.log("Ticket cree:", t);
            setTicket(t);

            const checkout = new window.monerisCheckout();
            checkout.setMode(USE_PROD ? "prod" : "qa");
            checkout.setCheckoutDiv("moneris-checkout-live");

            checkout.setCallback("payment_receipt", async (data) => {
                console.log("PAYMENT_RECEIPT - Formation Live");
                console.log("Data:", data);
                console.log("Ticket:", t);

                try {
                    await handlePaymentSuccess(t);
                } catch (error) {
                    console.error("Erreur dans handlePaymentSuccess:", error);
                }
            });

            checkout.setCallback("cancel_transaction", () => {
                console.warn("Paiement annule");
                setErr("Paiement annule par l utilisateur");
                onError && onError(new Error("Paiement annule"));
            });

            checkout.setCallback("error_event", (error) => {
                console.error("Erreur Moneris:", error);
                setErr("Erreur lors du paiement");
                onError && onError(error);
            });

            checkout.setCallback("page_loaded", () => {
                console.log("Page Moneris chargee");
            });

            checkout.startCheckout(t);
        } catch (e) {
            console.error("Erreur paiement Moneris (Live Training):", e);
            setErr(e?.message || "Erreur lors du paiement");
            onError && onError(e);
        } finally {
            setLoading(false);
        }
    };

    const handlePaymentSuccess = async (ticketId, retryCount = 0) => {
        const MAX_RETRIES = 5;
        const DELAY_MS = 2000;

        setProcessing(true);
        setErr("");

        try {
            let userId = localStorage.getItem('userId');

            if (!userId) {
                userId = sessionStorage.getItem('userId');
            }

            if (!userId) {
                const userStr = localStorage.getItem('user');
                if (userStr) {
                    const user = JSON.parse(userStr);
                    userId = user.id;
                }
            }

            if (!userId) {
                throw new Error("Utilisateur non identifie. Veuillez vous reconnecter.");
            }

            console.log(`Tentative ${retryCount + 1}/${MAX_RETRIES} - Verification...`);

            if (retryCount === 0) {
                console.log("Attente de 3 secondes pour que Moneris finalise...");
                await new Promise(resolve => setTimeout(resolve, 3000));
            }

            const response = await liveTrainingService.monerisVerifyAndProcessLiveTraining(
                ticketId,
                userId,
                liveTraining.id
            );

            console.log("Reponse backend:", response);

            if (response.success && response.data?.purchase) {
                console.log("Achat formation live enregistre:", response.data.purchase);

                onSuccess && onSuccess({
                    ticket: ticketId,
                    purchase: response.data.purchase,
                    verification: response.data.verification,
                    liveTraining: liveTraining,
                });
            }
            else if (
                response.data?.verification?.status === "DECLINED" &&
                response.data?.verification?.message?.includes("not complete") &&
                retryCount < MAX_RETRIES
            ) {
                console.log(`Transaction pas encore finalisee, ressai dans ${DELAY_MS / 1000}s...`);
                await new Promise(resolve => setTimeout(resolve, DELAY_MS));
                return handlePaymentSuccess(ticketId, retryCount + 1);
            }
            else {
                throw new Error(response.message || "Paiement non approuve");
            }

        } catch (error) {
            console.error("Erreur verification paiement formation live:", error);

            if (retryCount < MAX_RETRIES && error.code === 'ECONNABORTED') {
                console.log(`Erreur reseau, reessai dans ${DELAY_MS / 1000}s...`);
                await new Promise(resolve => setTimeout(resolve, DELAY_MS));
                return handlePaymentSuccess(ticketId, retryCount + 1);
            }

            setErr(error.response?.data?.message || error.message || "Erreur lors de la verification du paiement");
            onError && onError(error);
        } finally {
            if (retryCount === 0 || retryCount >= MAX_RETRIES) {
                setProcessing(false);
            }
        }
    };

    return (
        <div>
            {/* Bandeau d information */}
            <div
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                    marginBottom: 12,
                    padding: 14,
                    background: 'linear-gradient(135deg, #fff5f5 0%, #ffe5e8 100%)',
                    border: '1px solid #ffb3ba',
                    borderRadius: 12,
                    boxShadow: '0 1px 2px rgba(0,0,0,0.03)',
                }}
            >
                <div style={{ fontSize: 24 }}><VideoCameraOutlined style={{ color: '#ee5a6f' }} /></div>
                <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 700, color: '#1a1a1a' }}>
                        Inscription Formation Live - Paiement securise Moneris
                    </div>
                    <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>
                        {liveTraining?.title} - <strong>{liveTraining?.price?.toFixed(2)} $ CAD</strong>
                    </div>
                </div>
                <Button
                    type="primary"
                    icon={<CreditCardOutlined />}
                    onClick={startPayment}
                    loading={loading}
                    disabled={processing}
                    style={{
                        background: 'linear-gradient(135deg, #ee5a6f 0%, #ff6b6b 100%)',
                        border: 'none',
                        fontWeight: 600,
                    }}
                >
                    {loading ? "Preparation..." : ticket ? "Relancer" : "Payer maintenant"}
                </Button>
            </div>

            {err && (
                <Alert
                    type="error"
                    message="Paiement Moneris"
                    description={err}
                    style={{ marginBottom: 12 }}
                    showIcon
                    closable
                    onClose={() => setErr("")}
                />
            )}

            {processing && (
                <Alert
                    type="info"
                    message="Traitement en cours"
                    description={
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <Spin size="small" />
                            <span>Verification du paiement et inscription...</span>
                        </div>
                    }
                    style={{ marginBottom: 12 }}
                    showIcon
                    icon={<CheckCircleOutlined />}
                />
            )}

            {/* ⭐ PLACEHOLDER EN DEHORS du div Moneris (affiche tant que pas de ticket) */}
            {!ticket && !loading && (
                <div style={{
                    background: 'white',
                    border: '1px dashed #ffb3ba',
                    borderRadius: 16,
                    padding: '40px 20px',
                    textAlign: 'center',
                    marginBottom: 12,
                }}>
                    <div style={{
                        width: 80,
                        height: 80,
                        borderRadius: '50%',
                        background: 'linear-gradient(135deg, #fff5f5 0%, #ffe5e8 100%)',
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        marginBottom: 20,
                        fontSize: 40,
                    }}>
                        🔒
                    </div>
                    <h3 style={{ fontSize: 20, fontWeight: 700, margin: '0 0 12px' }}>
                        Paiement securise Moneris
                    </h3>
                    <p style={{ color: '#6b7280', maxWidth: 400, margin: '0 auto 20px' }}>
                        Cliquez sur "Payer maintenant" en haut pour afficher le formulaire de paiement securise.
                    </p>
                    <div style={{
                        background: '#fafafa',
                        padding: 16,
                        borderRadius: 12,
                        maxWidth: 360,
                        margin: '0 auto 16px',
                    }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <span style={{ fontWeight: 700 }}>Total a payer :</span>
                            <span style={{ color: '#ee5a6f', fontWeight: 800, fontSize: 22 }}>
                                {liveTraining?.price?.toFixed(2)} $ CAD
                            </span>
                        </div>
                    </div>
                    <div style={{ fontSize: 12, color: '#6b7280' }}>
                        VISA - Mastercard - AMEX - DISCOVER
                    </div>
                </div>
            )}

            {/* Spinner pendant le chargement du SDK */}
            {loading && (
                <div style={{
                    background: 'white',
                    border: '1px dashed #ffb3ba',
                    borderRadius: 16,
                    padding: '60px 20px',
                    textAlign: 'center',
                    marginBottom: 12,
                }}>
                    <Spin size="large" />
                    <p style={{ marginTop: 20, color: '#6b7280' }}>
                        Preparation du paiement securise...
                    </p>
                </div>
            )}

            {/* ⭐ DIV MONERIS - VIDE - SDK Moneris va le remplir */}
            <div
                id="moneris-checkout-live"
                ref={containerRef}
                style={{
                    width: "100%",
                    minHeight: ticket ? 560 : 0,
                    background: ticket ? "#fff" : "transparent",
                    borderRadius: 16,
                    boxShadow: ticket ? '0 2px 10px rgba(0,0,0,0.04)' : 'none',
                }}
            />

            <div style={{ textAlign: 'center', color: '#6b7280', fontSize: 12, marginTop: 10 }}>
                Paiement 100% securise via Moneris - Besoin d aide ? <strong>+212 661 571 360</strong>
            </div>
        </div>
    );
};

export default MonerisPaymentLiveTraining;